import { createContext, useContext, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "em-theme";
export const THEMES = ["light", "dark", "orange", "green", "blue", "graphic"];

export const useTheme = () => useContext(ThemeContext);

const read = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(stored) ? stored : null;
  } catch {
    return null;
  }
};

export const ThemeProvider = ({ children }) => {
  // "Default" theme = the original light/dark shell; "orange" = the ember
  // neumorphic re-skin. First-time users get the workgroup default (applied
  // by the dashboard via applyWorkgroupDefault) — light until it loads.
  const [theme, setThemeState] = useState(() => read() || "light");

  const setTheme = useCallback((next) => {
    const value = THEMES.includes(next) ? next : "dark";
    setThemeState(value);
    try { localStorage.setItem(STORAGE_KEY, value); } catch { /* ignore */ }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const value = prev === "light" ? "dark" : "light";
      try { localStorage.setItem(STORAGE_KEY, value); } catch { /* ignore */ }
      return value;
    });
  }, []);

  /** Workgroup default — only used when this device has no explicit choice. */
  const applyWorkgroupDefault = useCallback((def) => {
    if (read()) return;
    setThemeState(THEMES.includes(def) ? def : "light");
  }, []);

  /** Admin "push": a one-shot global change. Applied once per device (tracked
   *  by the push timestamp); the user may pick their own theme again after. */
  const applyPush = useCallback((push) => {
    if (!push?.at || !THEMES.includes(push.theme)) return;
    try {
      if (localStorage.getItem(`${STORAGE_KEY}-push-seen`) === push.at) return;
      localStorage.setItem(`${STORAGE_KEY}-push-seen`, push.at);
      localStorage.setItem(STORAGE_KEY, push.theme);
    } catch { /* ignore */ }
    setThemeState(push.theme);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, applyWorkgroupDefault, applyPush, hasExplicitChoice: !!read() }}>{children}</ThemeContext.Provider>
  );
};
